import { DeprecatedGemColors } from './DeprecatedGemColors'
import { type ColorSet } from './types'
import { wideGamutColor } from './utils/wideGamutColor'
import {
  DarkThemeColorValues,
  GemColors,
  LightThemedColorValues,
  ThemedColors,
} from './variables'

/**
 * @note **Do not use this object if a CSS Variable is possible. **
 * @example
 * color: `var(${ThemedColors.Primary1})`
 *
 * Object to access standard themed colors used in the Gem Design System.
 *
 * @note `#E6E9EB` (`#e6e9eb`) is not a valid Gem color; it’s the default gray
 *   in Figma. The intended color is NL300 (`Color[theme].Surface.N300`). See
 *   https://opal.slack.com/archives/C01L6C61KB7/p1711477122355109?thread_ts=1711476337.509819&cid=C01L6C61KB7
 * @note Values in `Color` can’t be passed to the `color` npm package. `Color`
 * values use the CSS `color()` function, which the `color` package can’t parse.
 * If you need to use the `color` package for something that can’t be done in
 * plain CSS you’ll have to use `GemColors` directly and handle theming logic.
 *
 * @example
 * backgroundColor: Color[theme]['Color/Surface/Surface-1']
 */
export const Color = {
  dark: {
    'Color/Primary/Primary-1': DarkThemeColorValues[ThemedColors.Primary1],
    'Color/Primary/Primary-2': DarkThemeColorValues[ThemedColors.Primary2],
    'Color/Primary/Primary-3': DarkThemeColorValues[ThemedColors.Primary3],
    'Color/Primary/Primary-4': DarkThemeColorValues[ThemedColors.Primary4],
    'Color/Primary/Primary-5': DarkThemeColorValues[ThemedColors.Primary5],
    'Color/Primary/Primary-6': DarkThemeColorValues[ThemedColors.Primary6],
    'Color/Surface/Surface-1': DarkThemeColorValues[ThemedColors.Surface1],
    'Color/Surface/Surface-2': DarkThemeColorValues[ThemedColors.Surface2],
    'Color/Surface/Surface-3': DarkThemeColorValues[ThemedColors.Surface3],
    'Color/Surface/Surface-4': DarkThemeColorValues[ThemedColors.Surface4],
    'Color/Warning/Warning-1': DarkThemeColorValues[ThemedColors.Warning1],
    'Strokes/Error': DarkThemeColorValues[ThemedColors.StrokeError1],
    'Strokes/Primary-2': DarkThemeColorValues[ThemedColors.StrokePrimary2],
    'Strokes/Secondary-1': DarkThemeColorValues[ThemedColors.StrokeSecondary1],
    'Strokes/Secondary-2': DarkThemeColorValues[ThemedColors.StrokeSecondary2],
    'Strokes/Secondary-4': DarkThemeColorValues[ThemedColors.StrokeSecondary4],
    'Text/Primary': DarkThemeColorValues[ThemedColors.TextPrimary],
    'Text/Secondary': DarkThemeColorValues[ThemedColors.TextSecondary],
    'Text/Contrast': DarkThemeColorValues[ThemedColors.TextContrast],
    'Text/Links': DarkThemeColorValues[ThemedColors.TextLinks],
    'Text/Error': DarkThemeColorValues[ThemedColors.TextError],
    'Text/Warning': DarkThemeColorValues[ThemedColors.TextWarning],
    'Icons/Primary': DarkThemeColorValues[ThemedColors.IconPrimary],
    'Icons/Secondary': DarkThemeColorValues[ThemedColors.IconSecondary],
    'Icons/Error': DarkThemeColorValues[ThemedColors.IconError],
    'Icons/Success': DarkThemeColorValues[ThemedColors.IconSuccess],
    'Icons/Warning': DarkThemeColorValues[ThemedColors.IconWarning],
    'Icons/Published': DarkThemeColorValues[ThemedColors.IconPublished],

    /**
     * @deprecated Prefer color variables; if the color you need doesn’t have a
     * variable in Figma call `wideGamutColor(GemColors.ABC)` in your code.
     */
    Surface: {
      // accent
      A000: DeprecatedGemColors.BD000,
      A100: wideGamutColor('#374257'),
      A200: GemColors.B400,
      A450: GemColors.B450,
      // neutral
      N000: GemColors.N900,
      N100: GemColors.N800,
      N200: GemColors.N700,
      N300: GemColors.N600,
      N400: GemColors.N500,
      N500: GemColors.N400,
      N600: wideGamutColor('#898A8A'),
      N999: wideGamutColor('#6D6F7A'),
      // secondary
      G100: GemColors.G300,
      P100: GemColors.P300,
      R100: GemColors.R300,
    },
    /**
     * @deprecated Prefer `Text/xyz` color variables; if the color you need
     * doesn’t have a variable in Figma call `wideGamutColor(GemColors.XYZ)` in
     * your code.
     */
    Text: {
      Accent: DeprecatedGemColors.DMBLUE,
      Default: GemColors.N000,
      Gray: DeprecatedGemColors.DMGRAY,
      InvertedDefault: DeprecatedGemColors.DMBLACK,
      Warning: DeprecatedGemColors.DMRED,
    },
    /**
     * @deprecated Prefer `Icons/xyz` color variables; if the color you need
     * doesn’t have a variable in Figma call `wideGamutColor(GemColors.XYZ)` in
     * your code.
     */
    Icon: {
      Primary: GemColors.N000,
      Secondary: GemColors.N200,
    },
    /** @deprecated Use `Overlay` from `@opallabs/gem` */
    Translucent: {
      // accent
      A00: wideGamutColor(GemColors.B400, 0.0),
      A04: wideGamutColor(GemColors.B400, 0.04),
      A08: wideGamutColor(GemColors.B400, 0.08),
      A12: wideGamutColor(GemColors.B400, 0.12),
      A16: wideGamutColor(GemColors.B400, 0.16),
      A24: wideGamutColor(GemColors.B400, 0.24),
      A36: wideGamutColor(GemColors.B400, 0.36),
      A48: wideGamutColor(GemColors.B400, 0.48),
      A50: wideGamutColor(GemColors.B400, 0.5),
      // neutral
      N00: wideGamutColor('#000000', 0.0),
      N02: wideGamutColor('#000000', 0.02),
      N06: wideGamutColor('#000000', 0.06),
      N08: wideGamutColor('#000000', 0.08),
      N12: wideGamutColor('#000000', 0.12),
      N16: wideGamutColor('#000000', 0.16),
      N24: wideGamutColor('#000000', 0.24),
      N36: wideGamutColor('#000000', 0.36),
      N48: wideGamutColor('#000000', 0.48),
      N72: wideGamutColor('#000000', 0.72),
    },
  } satisfies ColorSet,

  light: {
    'Color/Primary/Primary-1': LightThemedColorValues[ThemedColors.Primary1],
    'Color/Primary/Primary-2': LightThemedColorValues[ThemedColors.Primary2],
    'Color/Primary/Primary-3': LightThemedColorValues[ThemedColors.Primary3],
    'Color/Primary/Primary-4': LightThemedColorValues[ThemedColors.Primary4],
    'Color/Primary/Primary-5': LightThemedColorValues[ThemedColors.Primary5],
    'Color/Primary/Primary-6': LightThemedColorValues[ThemedColors.Primary6],
    'Color/Surface/Surface-1': LightThemedColorValues[ThemedColors.Surface1],
    'Color/Surface/Surface-2': LightThemedColorValues[ThemedColors.Surface2],
    'Color/Surface/Surface-3': LightThemedColorValues[ThemedColors.Surface3],
    'Color/Surface/Surface-4': LightThemedColorValues[ThemedColors.Surface4],
    'Color/Warning/Warning-1': LightThemedColorValues[ThemedColors.Warning1],
    'Strokes/Error': LightThemedColorValues[ThemedColors.StrokeError1],
    'Strokes/Primary-2': LightThemedColorValues[ThemedColors.StrokePrimary2],
    'Strokes/Secondary-1':
      LightThemedColorValues[ThemedColors.StrokeSecondary1],
    'Strokes/Secondary-2':
      LightThemedColorValues[ThemedColors.StrokeSecondary2],
    'Strokes/Secondary-4':
      LightThemedColorValues[ThemedColors.StrokeSecondary4],
    'Text/Primary': LightThemedColorValues[ThemedColors.TextPrimary],
    'Text/Secondary': LightThemedColorValues[ThemedColors.TextSecondary],
    'Text/Contrast': LightThemedColorValues[ThemedColors.TextContrast],
    'Text/Links': LightThemedColorValues[ThemedColors.TextLinks],
    'Text/Error': LightThemedColorValues[ThemedColors.TextError],
    'Text/Warning': LightThemedColorValues[ThemedColors.TextWarning],
    'Icons/Primary': LightThemedColorValues[ThemedColors.IconPrimary],
    'Icons/Secondary': LightThemedColorValues[ThemedColors.IconSecondary],
    'Icons/Error': LightThemedColorValues[ThemedColors.IconError],
    'Icons/Success': LightThemedColorValues[ThemedColors.IconSuccess],
    'Icons/Warning': LightThemedColorValues[ThemedColors.IconWarning],
    'Icons/Published': LightThemedColorValues[ThemedColors.IconPublished],

    /**
     * @deprecated Prefer color variables; if the color you need doesn’t have a
     * variable in Figma call `wideGamutColor(GemColors.ABC)` in your code.
     */
    Surface: {
      // accent
      A000: GemColors.B150,
      A100: GemColors.B250,
      A200: GemColors.B450,
      A450: GemColors.B550,
      // neutral
      N000: GemColors.N000,
      N100: GemColors.N050,
      N200: GemColors.N100,
      N300: GemColors.N150,
      N400: GemColors.N200,
      N500: GemColors.N250,
      N600: GemColors.N300,
      N999: wideGamutColor('#8F92A1'),
      // secondary
      G100: GemColors.G300,
      R100: GemColors.R400,
    },
    /**
     * @deprecated Prefer `Text/xyz` color variables; if the color you need
     * doesn’t have a variable in Figma call `wideGamutColor(GemColors.XYZ)` in
     * your code.
     */
    Text: {
      Accent: DeprecatedGemColors.LMBLUE,
      Default: DeprecatedGemColors.LMBLACK,
      Gray: DeprecatedGemColors.LMGRAY,
      InvertedDefault: GemColors.N000,
      Warning: DeprecatedGemColors.LMRED,
    },
    /**
     * @deprecated Prefer `Icons/xyz` color variables; if the color you need
     * doesn’t have a variable in Figma call `wideGamutColor(GemColors.XYZ)` in
     * your code.
     */
    Icon: {
      Primary: GemColors.N999,
      Secondary: GemColors.N350,
    },
    /** @deprecated Use `Overlay` from @opallabs/gem */
    Translucent: {
      // accent
      A00: wideGamutColor(GemColors.B450, 0.0),
      A04: wideGamutColor(GemColors.B450, 0.04),
      A08: wideGamutColor(GemColors.B450, 0.08),
      A12: wideGamutColor(GemColors.B450, 0.12),
      A16: wideGamutColor(GemColors.B450, 0.16),
      A24: wideGamutColor(GemColors.B450, 0.24),
      A36: wideGamutColor(GemColors.B450, 0.36),
      A48: wideGamutColor(GemColors.B450, 0.48),
      A50: wideGamutColor(GemColors.B450, 0.5),
      // neutral
      N00: wideGamutColor('#FFFFFF', 0.0),
      N02: wideGamutColor('#FFFFFF', 0.02),
      N06: wideGamutColor('#FFFFFF', 0.06),
      N08: wideGamutColor('#FFFFFF', 0.08),
      N12: wideGamutColor('#FFFFFF', 0.12),
      N16: wideGamutColor('#FFFFFF', 0.16),
      N24: wideGamutColor('#FFFFFF', 0.24),
      N36: wideGamutColor('#FFFFFF', 0.36),
      N48: wideGamutColor('#FFFFFF', 0.48),
      N72: wideGamutColor('#FFFFFF', 0.72),
    },
  } satisfies ColorSet,
} as const

/**
 * Sometimes designs are built using the ‘opposite’ / inverted theme. (For
 * example, a light-theme primary button’s hover state uses dark-theme
 * `AccentOpacity`.)
 */
/** @deprecated Use `Overlay` from @opallabs/gem */
export const OppositeTheme = {
  dark: 'light',
  light: 'dark',
} as const
export { GemColors }
